import {Menu, MenuItem} from "navigation-common/types/navigation-types";
import {TreeData, TreeItem} from "@atlaskit/tree";
import {ItemId} from "@atlaskit/tree/types";

class NavigationHelperService {
    public buildNewMenuItem(key: string, level: number, name?: string, link?: string): MenuItem {
        return {
            key: key,
            level: level,
            name: name ? name : "",
            link: link ? link : "",
            subMenuItems: [],
            pageId: "",
            spaceKey: ""
        } as MenuItem
    }

    public buildNextKey(oldKey: string, level: number): string {
        if (!oldKey) {
            return "";
        }
        let newKey = oldKey.slice();

        let parts = newKey.split(".");
        let newDigit: string = String(parseInt(parts[level - 1]) + 1);
        if (level === 1) {
            newKey = newDigit + newKey.substr(newKey.indexOf("."));
        } else if (level === 2) {
            newKey = this.replaceMiddlePartOfKey(newKey, newDigit);
        } else if (level === 3) {
            newKey = newKey.substr(0, newKey.lastIndexOf(".") + 1) + newDigit
        }

        return newKey;
    }

    public buildKeyAtIndex(level: number, precedingKey: string): string {
        let newKey = "";
        if (level === 1) {
            let index = precedingKey.substr(0, precedingKey.indexOf("."));
            newKey = String(parseInt(index) + 1) + ".0.0";
        } else if (level === 2) {
            const parts = precedingKey.split(".");
            newKey = this.replaceMiddlePartOfKey(precedingKey, String(parseInt(parts[1]) + 1));
        } else if (level === 3) {
            let index = precedingKey.substr(precedingKey.lastIndexOf(".") + 1);
            newKey = precedingKey.substr(0, precedingKey.lastIndexOf(".") + 1) + String(parseInt(index) + 1);
        }
        return newKey;
    }

    public insertMenuItemIntoList(menu: Menu | undefined, newMenuItem: MenuItem | undefined) {
        if (!menu || !newMenuItem) {
            return undefined;
        }

        const indexToInsert = this.getIndexForNewMenuItem(newMenuItem.key, newMenuItem.level);
        const parent = navigationHelperService.getParentOfMenuItem(menu, newMenuItem);
        if (parent) {
            parent.subMenuItems?.splice(indexToInsert, 0, newMenuItem);
            this.resetAllKeysUnderParent(parent);
        } else {
            menu.menuItems.splice(indexToInsert, 0, newMenuItem);
            this.resetAllKeys(menu);
        }
    }

    public removeMenuItemsWithChildsFromList(menu: Menu | undefined, menuItem: MenuItem | undefined) {
        if (!menu || !menuItem) {
            return undefined;
        }

        if (menuItem.level === 1) {
            menu.menuItems = menu.menuItems.filter((subMenuItem) => {
                return subMenuItem.key !== menuItem.key;
            })

            this.resetAllKeys(menu);
        } else if (menuItem.level > 1) {
            const parent: MenuItem | undefined = this.getParentOfMenuItem(menu, menuItem);

            if (parent) {
                parent.subMenuItems = parent.subMenuItems?.filter((subMenuItem) => {
                    return subMenuItem.key !== menuItem.key;
                })

                this.resetAllKeysUnderParent(parent);
            }
        }
    }

    public getParentOfMenuItem(menu: Menu | undefined, menuItem: MenuItem | undefined): MenuItem | undefined {
        if (!menuItem || !menu) {
            return undefined;
        }

        let parentKey: string = "";
        const indexOfZero = menuItem.key.indexOf(".0");
        if (indexOfZero === -1) {
            parentKey = menuItem.key.substr(0, menuItem.key.lastIndexOf(".")) + ".0";
        } else if (indexOfZero >= menuItem.key.lastIndexOf(".")) {
            parentKey = menuItem.key.substr(0, menuItem.key.indexOf(".")) + ".0.0";
        }

        return this.getMenuItemWithKey(menu, parentKey);
    }

    public getMenuItemWithKey(menu: Menu, key: string): MenuItem | undefined {
        if (!menu || !key) {
            return undefined;
        }
        const indexOfZero = key.indexOf("0");
        let foundMenuItem: MenuItem | undefined;

        if (indexOfZero === -1) {
            const numberParts = key.split(".");
            foundMenuItem = menu.menuItems?.[parseInt(numberParts[0]) - 1]?.subMenuItems?.[parseInt(numberParts[1]) - 1]?.subMenuItems?.find((menuItem) => menuItem.key === key);
        } else if (indexOfZero > key.lastIndexOf(".")) {
            const numberParts = key.split(".");
            foundMenuItem = menu.menuItems?.[parseInt(numberParts[0]) - 1]?.subMenuItems?.find((menuItem) => menuItem.key === key);
        } else if (indexOfZero > key.indexOf(".")) {
            foundMenuItem = menu.menuItems.find((menuItem) => menuItem.key === key);
        }

        return foundMenuItem;
    }

    public countMenuItems(menu: Menu | undefined): number {
        if (menu) {
            let counter = 0;
            counter += menu.menuItems.length;
            menu.menuItems.map((menuItem) => {
                counter += menuItem.subMenuItems ? menuItem.subMenuItems.length : 0;
                menuItem.subMenuItems?.map((subMenuItem) => {
                    counter += subMenuItem.subMenuItems ? subMenuItem.subMenuItems.length : 0;
                })
            })

            return counter;
        }
        return -1;
    }

    private replaceMiddlePartOfKey(key: string, replacement: string): string {
        let parts = key.split(".");
        const startIndexFrom = parts[0].length + parts[1].length;
        return key.substr(0, key.indexOf(".") + 1) + replacement + key.substr(key.indexOf(".", startIndexFrom));
    }

    private getIndexForNewMenuItem(key: string, level: number): number {
        const numberParts = key.split(".");

        return parseInt(numberParts[level - 1]) - 1;
    }

    public resetAllKeys(menu: Menu | undefined) {
        if (!menu) {
            return;
        }

        menu.menuItems?.map((menuItem, index1) => {
            menuItem.key = String(index1 + 1) + ".0.0";
            this.resetAllKeysUnderParent(menuItem);
        })
    }

    private resetAllKeysUnderParent(parent: MenuItem | undefined) {
        if (!parent) {
            return;
        }
        const keyParts = parent.key.split(".");

        if (parent.level === 1) {
            parent.subMenuItems?.map((subMenuItem, index2) => {
                subMenuItem.key = keyParts[0] + "." + String(index2 + 1) + ".0";
                subMenuItem.subMenuItems?.map((subSubMenuItem, index3) => {
                    subSubMenuItem.key = keyParts[0] + "." + String(index2 + 1) + "." + String(index3 + 1);
                });
            });
        } else if (parent.level == 2) {
            parent.subMenuItems?.map((subMenuItem, index3) => {
                subMenuItem.key = keyParts[0] + "." + keyParts[1] + "." + String(index3 + 1);
            });
        }
    }

    public updateMenuItemLevelUnderneathMenuItem(menuItem: MenuItem, newLvl: number) {
        menuItem.level = newLvl;

        if (menuItem.subMenuItems && menuItem.subMenuItems.length > 0) {
            menuItem.subMenuItems.map(subMenuItem => {
                subMenuItem.level = newLvl + 1;

                if (subMenuItem.subMenuItems  && subMenuItem.subMenuItems.length > 0) {
                    subMenuItem.subMenuItems.map(subSubMenuItem => {
                        subSubMenuItem.level = newLvl + 2;
                    });
                }
            });
        }
    }
}

export const navigationHelperService = new NavigationHelperService();
